
  import { defineComponent, onMounted, ref, computed, watch } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import Multiselect from '@vueform/multiselect';
  import { ProductObject } from '@/store/modules/clients/ProductsModule';

  export default defineComponent({
    name: 'product-creating',
    components: {
      ErrorMessage,
      Field,
      Form,
      Multiselect,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();
      const selectedClient = ref<number>(0);

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('productUpdating'), [
          translate('clients'),
        ]);
      });

      watch(
        () => selectedClient.value,
        async () => {
          if (selectedClient.value) {
            selectedOffice.value = [];
            await store.dispatch(
              Actions.GET_SHIPMENT_GW_OFFICES,
              selectedClient.value
            );
          }
        }
      );

      const { data } = await store.dispatch(
        Actions.GET_PRODUCT,
        route.params.id
      );

      const product = ref<ProductObject>(data.data);

      const getProductImage = () => {
        const imageUrl = product.value.image;
        if (imageUrl) {
          return imageUrl;
        } else {
          return '/media/avatars/blank.png';
        }
      };

      const selectedOffice = ref<Array<string>>([]);
      selectedOffice.value = Object.values(product.value.offices);

      const titleAr = () => {
        return product.value.name.find((i) => {
          if (i.languageCode === 'ar') {
            return i.title;
          }
        }).title;
      };

      const titleEn = () => {
        return product.value.name.find((i) => {
          if (i.languageCode === 'en') {
            return i.title;
          }
        }).title;
      };

      const titleKu = () => {
        return product.value.name.find((i) => {
          if (i.languageCode === 'ku') {
            return i.title;
          }
        }).title;
      };

      const descriptionKu = () => {
        return product.value.description.find((i) => {
          if (i.languageCode === 'ku') {
            return i.title;
          }
        }).title;
      };

      const descriptionEn = () => {
        return product.value.description.find((i) => {
          if (i.languageCode === 'en') {
            return i.title;
          }
        }).title;
      };

      const descriptionAr = () => {
        return product.value.description.find((i) => {
          if (i.languageCode === 'ar') {
            return i.title;
          }
        }).title;
      };

      const goBack = () => {
        router.back();
      };

      const iqdPrice = ref(product.value.iqdPrice);
      const usdPrice = ref(product.value.usdPrice);

      const currency = await store.dispatch(Actions.GET_CURRENCY);

      const iqdExchangeRate = currency.find(
        (item) => item.code === 'iqd'
      ).exchangeRate;

      const convertToIQ = (usdPrice) => {
        iqdPrice.value = usdPrice * iqdExchangeRate;
      };

      const shpmentSchema = Yup.object().shape({
        titleAr: Yup.string()
          .required(() => translate('AR_TITLE_IS_REQUIRED_FIELD'))
          .label('Title Ar'),
        titleEn: Yup.string()
          .required(() => translate('EN_TITLE_IS_REQUIRED_FIELD'))
          .label('Title En'),
        titleKu: Yup.string()
          .required(() => translate('KU_TITLE_IS_REQUIRED_FIELD'))
          .label('Title Ku'),
        descriptionAr: Yup.string()
          .required(() => translate('AR_DESCRIPTION_IS_REQUIRED_FIELD'))
          .label('Description Ar'),
        descriptionEn: Yup.string()
          .required(() => translate('EN_DESCRIPTION_IS_REQUIRED_FIELD'))
          .label('Description En'),
        descriptionKu: Yup.string()
          .required(() => translate('KU_DESCRIPTION_IS_REQUIRED_FIELD'))
          .label('Description Ku'),
        price: Yup.string()
          .required(() => translate('PRICE_IS_REQUIRED_FIELD'))
          .label('Price'),
        clientId: Yup.string()
          .required(() => translate('CLIENT_IS_REQUIRED_FIELD'))
          .label('clientId'),
      });
      const onSubmitCreate = async (values) => {
        values.name = [];
        values.description = [];
        values.name.push({ languageCode: 'en', title: values.titleEn });
        values.name.push({ languageCode: 'ar', title: values.titleAr });
        values.name.push({ languageCode: 'ku', title: values.titleKu });

        values.description.push({
          languageCode: 'ar',
          title: values.descriptionAr,
        });
        values.description.push({
          languageCode: 'en',
          title: values.descriptionEn,
        });
        values.description.push({
          languageCode: 'ku',
          title: values.descriptionKu,
        });

        delete values.titleEn;
        delete values.titleAr;
        delete values.titleKu;

        delete values.descriptionAr;
        delete values.descriptionEn;
        delete values.descriptionKu;

        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        let imageFormData: FormData | null = null;
        if (values.image) {
          imageFormData = new FormData();
          imageFormData.append('productImage', values.image);
        }
        delete values.image;

        values.officesIds = selectedOffice.value;
        values.price = Number(values.price);
        values.slug = 'product-one';
        values.isactive = true;

        delete values.priceIqd;

        await store.dispatch(Actions.UPDATE_PRODUCT, {
          data: values,
          id: product.value?.id,
        });
        if (product.value?.id && imageFormData) {
          await store.dispatch(Actions.UPLOAD_PRODUCT_IMAGE, {
            data: imageFormData,
            productId: product.value?.id,
          });
        }

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_PRODUCT'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'products-listing' });
          });
        }
      };
      await store.dispatch(
        Actions.GET_SHIPMENT_GW_OFFICES,
        product.value.clientId
      );
      await store.dispatch(Actions.GET_ALL_CLIENTS, { limit: 50 });

      const offices = computed(() => store.getters.gwOfficesList);
      const officesTitle = () => {
        return offices.value
          .filter((r) => r.deletedat === null)
          .map((r) => ({ title: r.titleTranslate, id: r.id }));
      };

      return {
        clients: computed(() => store.getters.allClientsList),
        onSubmitCreate,
        getProductImage,
        officesTitle,
        product,
        titleAr,
        titleEn,
        titleKu,
        convertToIQ,
        usdPrice,
        iqdPrice,
        descriptionAr,
        descriptionEn,
        descriptionKu,
        selectedClient,
        selectedOffice,
        submitButton,
        translate,
        shpmentSchema,
        goBack,
        can,
      };
    },
  });
